import { AlertTriangle, RefreshCw, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { DlqReasonBadge } from './badges';
import { formatDateTime } from './format';
import { DlqMessage } from '../../../services/api';

export interface DlqMessageDialogProps {
  message: DlqMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRequeue: (message: DlqMessage) => void;
  onDelete: (message: DlqMessage) => void;
  actionPending: boolean;
}

const REVALIDATION_HINT_REASONS = new Set(['schema_violation', 'invalid_json']);

/** Detail-Modal für eine Dead-Letter-Nachricht — Payload, Requeue, Verwerfen. */
export function DlqMessageDialog({ message, open, onOpenChange, onRequeue, onDelete, actionPending }: DlqMessageDialogProps) {
  if (!message) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            Dead-Letter-Nachricht
            <DlqReasonBadge reason={message.reason} />
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <span className="text-gray-500">Grund: </span>
            <DlqReasonBadge reason={message.reason} />
          </div>
          <div>
            <span className="text-gray-500">Ursprungs-Queue: </span>
            <span className="font-mono">service-errors</span>
          </div>
          <div>
            <span className="text-gray-500">Dead-lettered am: </span>
            {formatDateTime(message.deadLetteredAt)}
          </div>
          <div>
            <span className="text-gray-500">Zustellversuche: </span>
            {message.retryCount}
          </div>
        </div>

        <div>
          <p className="mb-1 text-xs font-medium text-gray-500">Payload</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded border bg-gray-50 p-3 font-mono text-xs">
            {message.payload}
          </pre>
        </div>

        {REVALIDATION_HINT_REASONS.has(message.reason) && (
          <div className="flex items-start gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Ohne Fix im sendenden Service besteht die Nachricht die Validierung erneut nicht und landet wieder in
              der DLQ.
            </span>
          </div>
        )}

        <DialogFooter>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600" disabled={actionPending}>
                <Trash2 className="mr-2 h-4 w-4" />
                Verwerfen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Nachricht verwerfen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Die Nachricht wird endgültig aus der Dead Letter Queue gelöscht und kann danach nicht mehr
                  erneut eingereiht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete(message)}>Verwerfen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={() => onRequeue(message)} disabled={actionPending}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Erneut einreihen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
